import { useState, useEffect } from "react";
import {
  Award, Loader2, AlertCircle, Plus, CheckCircle2, X,
  TrendingUp, Users, Star, FileText, Clock, Search,
  ExternalLink, ThumbsUp, ThumbsDown, RefreshCw,
} from "lucide-react";
import {
  useAdminGetCpdEntriesQuery,
  useAdminGetCpdSummaryQuery,
  useAdminCreateCpdEntryMutation,
  useAdminGetCpdSubmissionsQuery,
  useAdminApproveCpdSubmissionMutation,
  useAdminRejectCpdSubmissionMutation,
} from "../../cpd/api/cpdApi";
import { useAdminGetEventsQuery } from "../api/adminEventsApi";

import PageGate from "../../../components/PageGate";
import { PERMS } from "../../../utils/perms";
function extractError(err: any): string {
  if (err?.data?.errors) {
    const first = Object.values(err.data.errors)[0];
    return Array.isArray(first) ? (first[0] as string) : String(first);
  }
  return err?.data?.message ?? err?.data?.error ?? "An error occurred";
}

function fmt(d?: string | null) {
  if (!d) return "—";
  return new Date(d).toLocaleDateString("en-NG", { day: "numeric", month: "short", year: "numeric" });
}

const TYPE_BADGE: Record<string, string> = {
  event:     "bg-indigo-50 text-indigo-700 border-indigo-100",
  manual:    "bg-emerald-50 text-emerald-700 border-emerald-100",
  deduction: "bg-rose-50 text-rose-700 border-rose-100",
};

const STATUS_BADGE: Record<string, { cls: string; label: string }> = {
  processing: { cls: "bg-amber-50 text-amber-700 border-amber-200",      label: "Under Review" },
  approved:   { cls: "bg-emerald-50 text-emerald-700 border-emerald-200", label: "Approved" },
  rejected:   { cls: "bg-rose-50 text-rose-700 border-rose-200",          label: "Rejected" },
};

const SOURCE_BADGE: Record<string, { cls: string; label: string }> = {
  auto:   { cls: "bg-teal-50 text-teal-700 border-teal-100",      label: "Auto" },
  admin:  { cls: "bg-indigo-50 text-indigo-700 border-indigo-100", label: "Admin" },
  system: { cls: "bg-gray-50 text-gray-500 border-gray-100",       label: "System" },
};

type EntryForm = {
  user_id: string;
  points: string;
  activity_type: "manual" | "deduction";
  activity_date: string;
  notes: string;
};
const emptyEntry: EntryForm = { user_id: "", points: "", activity_type: "manual", activity_date: "", notes: "" };

type Tab = "ledger" | "submissions" | "leaderboard";

type ApproveModal = {
  id: number;
  title: string;
  points: number | null;
  hasEvent: boolean;       // false = manual claim needing an event attached
  claimedPoints?: number | null;
  organizer?: string | null;
};
type RejectModal  = { id: number; title: string };

// Hold off firing the search request until the admin stops typing — the
// membership-number search runs server-side across every page of results.
function useDebounced(value: string, delay = 400) {
  const [debounced, setDebounced] = useState(value);
  useEffect(() => {
    const t = setTimeout(() => setDebounced(value.trim()), delay);
    return () => clearTimeout(t);
  }, [value, delay]);
  return debounced;
}

function AdminCpdPageInner() {
  const [tab, setTab] = useState<Tab>("submissions");

  // — Ledger —
  const [entrySearch, setEntrySearch] = useState("");
  const entrySearchQ = useDebounced(entrySearch);
  const {
    data: entries = [],
    isLoading: loadingEntries,
    isFetching: fetchingEntries,
    refetch: refetchEntries,
  } = useAdminGetCpdEntriesQuery(entrySearchQ ? { search: entrySearchQ } : {});
  const [createEntry, { isLoading: creating }] = useAdminCreateCpdEntryMutation();
  const [showForm, setShowForm]   = useState(false);
  const [form, setForm]           = useState<EntryForm>(emptyEntry);
  const [formError, setFormError] = useState("");

  // — Submissions —
  const [subStatus, setSubStatus] = useState<string>("processing");
  const [subSearch, setSubSearch] = useState("");
  const subSearchQ = useDebounced(subSearch);
  const [subPage, setSubPage] = useState(1);
  // Reset to first page whenever the status filter or the search term changes.
  useEffect(() => { setSubPage(1); }, [subStatus, subSearchQ]);
  const {
    data: submissionsRaw,
    isLoading: loadingSubs,
    isFetching: fetchingSubs,
    refetch: refetchSubs,
  } = useAdminGetCpdSubmissionsQuery({
    ...(subStatus ? { status: subStatus } : {}),
    ...(subSearchQ ? { search: subSearchQ } : {}),
    page: subPage,
  });
  const submissions: any[] = submissionsRaw?.data ?? [];
  const subMeta = {
    current: submissionsRaw?.current_page ?? 1,
    last: submissionsRaw?.last_page ?? 1,
    total: submissionsRaw?.total ?? submissions.length,
    from: submissionsRaw?.from ?? 0,
    to: submissionsRaw?.to ?? 0,
  };

  const [approve, { isLoading: approving }] = useAdminApproveCpdSubmissionMutation();
  const [reject, { isLoading: rejecting }]  = useAdminRejectCpdSubmissionMutation();

  const [approveModal, setApproveModal] = useState<ApproveModal | null>(null);
  const [approveNotes, setApproveNotes]   = useState("");
  const [approveEventId, setApproveEventId] = useState("");

  // CPD events for attaching to a manual claim on approval.
  const { data: allEvents = [] } = useAdminGetEventsQuery();
  const cpdEvents = (allEvents as any[]).filter((ev: any) => ev.is_cpd);
  const [rejectModal, setRejectModal]   = useState<RejectModal | null>(null);
  const [rejectNotes, setRejectNotes]   = useState("");
  const [actionError, setActionError]   = useState("");

  // — Leaderboard —
  const { data: summary = [], isLoading: loadingSummary } = useAdminGetCpdSummaryQuery();

  // — Toast —
  const [msg, setMsg] = useState<{ type: "success" | "error"; text: string } | null>(null);
  const toast = (type: "success" | "error", text: string) => {
    setMsg({ type, text });
    setTimeout(() => setMsg(null), 4000);
  };

  const inputCls = "w-full border-b-2 border-gray-100 py-2 font-bold text-gray-800 outline-none focus:border-indigo-500 bg-transparent text-sm";

  const set = (k: keyof EntryForm) =>
    (e: React.ChangeEvent<HTMLInputElement | HTMLSelectElement | HTMLTextAreaElement>) =>
      setForm((p) => ({ ...p, [k]: e.target.value }));

  const handleCreateEntry = async () => {
    setFormError("");
    if (!form.user_id.trim()) { setFormError("User ID is required"); return; }
    if (!form.points.trim())  { setFormError("Points are required"); return; }
    if (!form.activity_date)  { setFormError("Date is required"); return; }
    try {
      await createEntry({
        user_id:       Number(form.user_id),
        points:        Number(form.points),
        activity_type: form.activity_type,
        activity_date: form.activity_date,
        notes:         form.notes || undefined,
      }).unwrap();
      toast("success", "CPD entry created.");
      setForm(emptyEntry);
      setShowForm(false);
    } catch (err) { setFormError(extractError(err)); }
  };

  const handleApprove = async () => {
    if (!approveModal) return;
    setActionError("");
    // Manual claim (no event): the admin must attach a CPD event; its points are
    // what gets awarded.
    if (!approveModal.hasEvent && !approveEventId) {
      setActionError("This is a manual claim — select a CPD event to attach before approving.");
      return;
    }
    try {
      await approve({
        id: approveModal.id,
        admin_notes: approveNotes || undefined,
        event_id: approveModal.hasEvent ? undefined : approveEventId,
      }).unwrap();
      toast("success", "Submission approved and CPD points allocated.");
      setApproveModal(null);
      setApproveNotes("");
      setApproveEventId("");
    } catch (err) { setActionError(extractError(err)); }
  };

  const handleReject = async () => {
    if (!rejectModal) return;
    setActionError("");
    try {
      await reject({ id: rejectModal.id, admin_notes: rejectNotes || undefined }).unwrap();
      toast("success", "Submission rejected.");
      setRejectModal(null);
      setRejectNotes("");
    } catch (err) { setActionError(extractError(err)); }
  };

  // Searching is done by the API (membership number, member name/email, title,
  // organiser) so it spans every page, not just the one on screen.
  const filteredSubs = submissions;

  const processingCount = (submissions as any[]).filter((s: any) => s.status === "processing").length;
  const totalPoints  = (entries as any[]).reduce(
    (s: number, e: any) => s + (e.activity_type === "deduction" ? -Math.abs(e.points) : Math.abs(e.points)), 0
  );

  return (
    <div className="min-h-screen bg-[#FDFDFD] pb-32">
      <div className="mx-auto max-w-[1400px] px-6 lg:px-10 py-12">

        {/* HEADER */}
        <header className="mb-12">
          <p className="text-[10px] font-black text-slate-400 uppercase tracking-[0.2em] mb-4">
            Admin / CPD Management
          </p>
          <h1 className="text-5xl lg:text-6xl font-[1000] text-slate-900 tracking-tight leading-none">
            CPD<span className="text-primary italic">.</span>
          </h1>
          <p className="text-slate-500 text-lg mt-4 font-medium max-w-xl">
            Manage member continuing professional development points — internal events, manual entries, and external certificate submissions.
          </p>
        </header>

        {/* STATS */}
        <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-10">
          {[
            { label: "Total Ledger Entries", val: (entries as any[]).length, icon: TrendingUp, color: "text-indigo-600 bg-indigo-50" },
            { label: "Members with CPD",     val: (summary as any[]).length,  icon: Users,     color: "text-emerald-600 bg-emerald-50" },
            { label: "Net Points (All)",      val: totalPoints.toFixed(0),    icon: Star,      color: "text-violet-600 bg-violet-50" },
            { label: "Under Review",  val: processingCount, icon: Clock, color: "text-amber-600 bg-amber-50" },
          ].map((s, i) => (
            <div key={i} className="bg-white border border-slate-100 p-6 rounded-[2rem] shadow-sm flex items-center gap-4">
              <div className={`w-11 h-11 rounded-2xl flex items-center justify-center ${s.color} shrink-0`}>
                <s.icon className="w-5 h-5" />
              </div>
              <div>
                <div className="text-[10px] font-black text-slate-400 uppercase tracking-widest mb-0.5">{s.label}</div>
                <div className="text-xl font-black text-slate-900">{s.val}</div>
              </div>
            </div>
          ))}
        </div>

        {/* GLOBAL TOAST */}
        {msg && (
          <div className={`mb-6 flex items-center gap-3 px-5 py-4 rounded-2xl border text-sm font-bold ${
            msg.type === "success" ? "bg-emerald-50 border-emerald-100 text-emerald-700" : "bg-rose-50 border-rose-100 text-rose-700"
          }`}>
            {msg.type === "success" ? <CheckCircle2 className="w-4 h-4 shrink-0" /> : <AlertCircle className="w-4 h-4 shrink-0" />}
            {msg.text}
            <button onClick={() => setMsg(null)} className="ml-auto"><X className="w-4 h-4" /></button>
          </div>
        )}

        {/* TABS */}
        <div className="flex flex-wrap gap-2 mb-8">
          {([
            { key: "submissions",  label: "External Submissions" },
            { key: "ledger",       label: "All Entries" },
            { key: "leaderboard",  label: "Leaderboard" },
          ] as { key: Tab; label: string }[]).map((t) => (
            <button
              key={t.key}
              onClick={() => setTab(t.key)}
              className={`px-5 py-2.5 rounded-2xl text-xs font-black uppercase tracking-widest transition-colors ${
                tab === t.key ? "bg-slate-900 text-white" : "bg-white border border-slate-100 text-slate-500 hover:border-slate-300"
              }`}
            >
              {t.label}
            </button>
          ))}

          <div className="ml-auto">
            <button
              onClick={() => { setShowForm(true); setFormError(""); setForm(emptyEntry); }}
              className="flex items-center gap-2 bg-slate-900 text-white font-black text-xs px-5 py-2.5 rounded-2xl hover:bg-primary transition-colors"
            >
              <Plus className="w-4 h-4" /> Manual Entry
            </button>
          </div>
        </div>

        {/* ── TAB: EXTERNAL SUBMISSIONS ── */}
        {tab === "submissions" && (
          <div className="space-y-6">
            {/* Status filter + search */}
            <div className="flex flex-col sm:flex-row gap-3 items-start sm:items-center">
              <div className="flex gap-2">
                {["processing", "approved", "rejected", ""].map((s) => (
                  <button
                    key={s || "all"}
                    onClick={() => setSubStatus(s)}
                    className={`px-4 py-2 rounded-xl text-[10px] font-black uppercase tracking-widest transition-colors ${
                      subStatus === s
                        ? "bg-slate-900 text-white"
                        : "bg-white border border-slate-100 text-slate-500 hover:border-slate-300"
                    }`}
                  >
                    {s || "All"}
                  </button>
                ))}
              </div>
              <div className="relative sm:ml-auto">
                <Search className="w-4 h-4 absolute left-3 top-1/2 -translate-y-1/2 text-slate-400 pointer-events-none" />
                <input
                  type="text"
                  placeholder="Search by membership no., name, title…"
                  value={subSearch}
                  onChange={(e) => setSubSearch(e.target.value)}
                  className="pl-9 pr-10 py-2.5 rounded-2xl border-2 border-slate-100 bg-white text-sm font-medium text-slate-700 placeholder-slate-300 focus:outline-none focus:border-slate-900 transition-colors w-80"
                />
                {subSearch && (
                  <button
                    onClick={() => setSubSearch("")}
                    className="absolute right-3 top-1/2 -translate-y-1/2 text-slate-300 hover:text-slate-600"
                    title="Clear search"
                  >
                    <X className="w-4 h-4" />
                  </button>
                )}
              </div>
              <button onClick={() => refetchSubs()} className="p-2.5 rounded-xl border border-slate-100 hover:bg-slate-50 transition-colors">
                <RefreshCw className="w-4 h-4 text-slate-400" />
              </button>
            </div>

            {loadingSubs ? (
              <div className="flex justify-center py-20"><Loader2 className="w-8 h-8 text-indigo-500 animate-spin" /></div>
            ) : filteredSubs.length === 0 ? (
              <div className="bg-white border border-slate-100 rounded-[2rem] p-16 text-center">
                <FileText className="w-10 h-10 text-slate-300 mx-auto mb-3" />
                <p className="text-slate-400 font-bold">
                  No {subStatus || ""} submissions{subSearchQ ? ` matching “${subSearchQ}”` : ""}.
                </p>
                {subSearchQ && (
                  <button onClick={() => setSubSearch("")} className="mt-3 text-xs font-black uppercase tracking-widest text-indigo-600 hover:text-indigo-800">
                    Clear search
                  </button>
                )}
              </div>
            ) : (
              <div className="bg-white rounded-[2rem] border border-slate-100 overflow-x-auto">
                <table className="w-full">
                  <thead>
                    <tr className="border-b border-slate-100 bg-slate-50/60">
                      <th className="text-left text-[10px] font-black text-slate-400 uppercase tracking-widest px-6 py-4">#</th>
                      <th className="text-left text-[10px] font-black text-slate-400 uppercase tracking-widest px-4 py-4">Member</th>
                      <th className="text-left text-[10px] font-black text-slate-400 uppercase tracking-widest px-4 py-4">Event / Activity</th>
                      <th className="text-left text-[10px] font-black text-slate-400 uppercase tracking-widest px-4 py-4 hidden md:table-cell">Date</th>
                      <th className="text-left text-[10px] font-black text-slate-400 uppercase tracking-widest px-4 py-4 hidden lg:table-cell">Claimed</th>
                      <th className="text-left text-[10px] font-black text-slate-400 uppercase tracking-widest px-4 py-4">Status</th>
                      <th className="text-right text-[10px] font-black text-slate-400 uppercase tracking-widest px-6 py-4">Actions</th>
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-slate-50">
                    {filteredSubs.map((s: any, idx: number) => (
                      <tr key={s.id} className="hover:bg-slate-50/60 transition-colors">
                        <td className="px-6 py-5 text-sm font-black text-slate-300">{idx + 1}</td>
                        <td className="px-4 py-5">
                          <div className="font-[900] text-slate-900 text-sm leading-tight">{s.member?.name ?? "—"}</div>
                          {s.member?.membership_no && (
                            <div className="text-[10px] font-black uppercase tracking-widest text-indigo-600 mt-1">
                              {s.member.membership_no}
                            </div>
                          )}
                          <div className="text-xs text-slate-400 font-medium mt-0.5">{s.member?.email}</div>
                        </td>
                        <td className="px-4 py-5">
                          <div className="font-bold text-slate-800 text-sm leading-tight">{s.title}</div>
                          <div className="text-xs text-slate-400 font-medium mt-0.5">{s.organizer}</div>
                          {s.certificate_url && (
                            <a
                              href={s.certificate_url}
                              target="_blank"
                              rel="noreferrer"
                              className="inline-flex items-center gap-1 text-[10px] font-black text-indigo-600 hover:text-indigo-800 mt-1"
                            >
                              <ExternalLink className="w-3 h-3" /> View Certificate
                            </a>
                          )}
                        </td>
                        <td className="px-4 py-5 hidden md:table-cell">
                          <span className="text-sm text-slate-500 font-medium">{fmt(s.activity_date)}</span>
                        </td>
                        <td className="px-4 py-5 hidden lg:table-cell">
                          <span className="text-sm font-black text-violet-600">
                            {s.cpd_points_claimed != null ? `${s.cpd_points_claimed} pts` : "—"}
                          </span>
                        </td>
                        <td className="px-4 py-5">
                          <div>
                            <span className={`text-[10px] font-black uppercase tracking-widest px-2.5 py-1 rounded-lg border ${STATUS_BADGE[s.status] ?? "bg-slate-100 text-slate-500 border-slate-200"}`}>
                              {s.status}
                            </span>
                            {s.status === "approved" && s.cpd_points_awarded != null && (
                              <div className="text-[10px] font-black text-emerald-600 mt-1">+{s.cpd_points_awarded} pts awarded</div>
                            )}
                          </div>
                        </td>
                        <td className="px-6 py-5">
                          <div className="flex items-center justify-end gap-2">
                            {s.status === "processing" ? (
                              <>
                                <button
                                  onClick={() => { setRejectModal({ id: s.id, title: s.title }); setRejectNotes(""); setActionError(""); }}
                                  className="px-3 py-2 rounded-xl border-2 border-slate-100 text-xs font-black text-rose-600 hover:bg-rose-50 hover:border-rose-100 transition-all flex items-center gap-1.5"
                                >
                                  <ThumbsDown className="w-3.5 h-3.5" /> Reject
                                </button>
                                <button
                                  onClick={() => {
                                    setApproveModal({
                                      id: s.id,
                                      title: s.title,
                                      points: s.event?.cpd_points ?? s.cpd_points_claimed,
                                      hasEvent: !!s.event,
                                      claimedPoints: s.cpd_points_claimed,
                                      organizer: s.organizer,
                                    });
                                    setApproveNotes("");
                                    setApproveEventId("");
                                    setActionError("");
                                  }}
                                  className="px-3 py-2 rounded-xl bg-slate-900 text-white text-xs font-black hover:bg-primary transition-all flex items-center gap-1.5"
                                >
                                  <ThumbsUp className="w-3.5 h-3.5" /> Approve
                                </button>
                              </>
                            ) : (s.reviewed_by || s.reviewed_at) ? (
                              <div className="text-right">
                                <div className="text-[11px] font-black text-slate-600">
                                  {s.status === "rejected" ? "Rejected by " : "Issued by "}
                                  {s.reviewed_by ?? "system (auto)"}
                                </div>
                                {s.reviewed_at && (
                                  <div className="text-[10px] text-slate-400 font-medium">
                                    {s.status === "rejected" ? "Rejected" : "Approved"} {fmt(s.reviewed_at)}
                                  </div>
                                )}
                              </div>
                            ) : (
                              <div className="text-xs text-slate-300 font-black">—</div>
                            )}
                          </div>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
                <div className="px-6 py-4 border-t border-slate-50">
                  <p className="text-xs font-bold text-slate-400">
                    Showing {subMeta.from}–{subMeta.to} of {subMeta.total} submission{subMeta.total !== 1 ? "s" : ""}
                    {subSearchQ ? ` matching “${subSearchQ}”` : ""}
                  </p>
                </div>
              </div>
            )}

            {/* Pagination */}
            {!loadingSubs && subMeta.last > 1 && (
              <div className="mt-4 flex flex-col sm:flex-row items-center justify-between gap-3">
                <p className="text-xs font-bold text-slate-400">
                  Page {subMeta.current} of {subMeta.last} · {subMeta.total} total
                </p>
                <div className="flex items-center gap-2">
                  <button
                    onClick={() => setSubPage((p) => Math.max(1, p - 1))}
                    disabled={subMeta.current <= 1 || fetchingSubs}
                    className="px-4 py-2 rounded-xl border-2 border-slate-100 text-xs font-black text-slate-600 hover:bg-slate-50 disabled:opacity-40 transition-all"
                  >
                    ← Prev
                  </button>
                  <span className="text-xs font-black text-slate-500 min-w-[3rem] text-center">
                    {fetchingSubs ? "…" : `${subMeta.current}/${subMeta.last}`}
                  </span>
                  <button
                    onClick={() => setSubPage((p) => Math.min(subMeta.last, p + 1))}
                    disabled={subMeta.current >= subMeta.last || fetchingSubs}
                    className="px-4 py-2 rounded-xl border-2 border-slate-100 text-xs font-black text-slate-600 hover:bg-slate-50 disabled:opacity-40 transition-all"
                  >
                    Next →
                  </button>
                </div>
              </div>
            )}
          </div>
        )}

        {/* ── TAB: LEDGER ── */}
        {tab === "ledger" && (
          <div className="space-y-4">
            <div className="flex items-center gap-3 justify-end">
              <div className="relative mr-auto">
                <Search className="w-4 h-4 absolute left-3 top-1/2 -translate-y-1/2 text-slate-400 pointer-events-none" />
                <input
                  type="text"
                  placeholder="Search by membership no., name, event…"
                  value={entrySearch}
                  onChange={(e) => setEntrySearch(e.target.value)}
                  className="pl-9 pr-10 py-2.5 rounded-2xl border-2 border-slate-100 bg-white text-sm font-medium text-slate-700 placeholder-slate-300 focus:outline-none focus:border-slate-900 transition-colors w-80"
                />
                {entrySearch && (
                  <button
                    onClick={() => setEntrySearch("")}
                    className="absolute right-3 top-1/2 -translate-y-1/2 text-slate-300 hover:text-slate-600"
                    title="Clear search"
                  >
                    <X className="w-4 h-4" />
                  </button>
                )}
              </div>
              {fetchingEntries && !loadingEntries && <Loader2 className="w-4 h-4 text-slate-300 animate-spin" />}
              <button onClick={() => refetchEntries()} className="p-2.5 rounded-xl border border-slate-100 hover:bg-slate-50 transition-colors">
                <RefreshCw className="w-4 h-4 text-slate-400" />
              </button>
            </div>
            {loadingEntries ? (
              <div className="flex justify-center py-20"><Loader2 className="w-8 h-8 text-indigo-500 animate-spin" /></div>
            ) : (entries as any[]).length === 0 ? (
              <div className="bg-white border border-slate-100 rounded-[2rem] p-16 text-center">
                <Award className="w-10 h-10 text-slate-300 mx-auto mb-3" />
                <p className="text-slate-400 font-bold">
                  {entrySearchQ ? `No CPD entries matching “${entrySearchQ}”.` : "No CPD entries yet."}
                </p>
                {entrySearchQ && (
                  <button onClick={() => setEntrySearch("")} className="mt-3 text-xs font-black uppercase tracking-widest text-indigo-600 hover:text-indigo-800">
                    Clear search
                  </button>
                )}
              </div>
            ) : (
              <div className="bg-white border border-slate-100 rounded-[2rem] overflow-x-auto">
                <table className="w-full">
                  <thead>
                    <tr className="border-b border-slate-100 bg-slate-50/60">
                      <th className="text-left text-[10px] font-black text-slate-400 uppercase tracking-widest px-6 py-4">#</th>
                      <th className="text-left text-[10px] font-black text-slate-400 uppercase tracking-widest px-4 py-4">Member</th>
                      <th className="text-left text-[10px] font-black text-slate-400 uppercase tracking-widest px-4 py-4">Activity</th>
                      <th className="text-left text-[10px] font-black text-slate-400 uppercase tracking-widest px-4 py-4 hidden md:table-cell">Date</th>
                      <th className="text-left text-[10px] font-black text-slate-400 uppercase tracking-widest px-4 py-4">Type</th>
                      <th className="text-right text-[10px] font-black text-slate-400 uppercase tracking-widest px-6 py-4">Points</th>
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-slate-50">
                    {(entries as any[]).map((e: any, idx: number) => (
                      <tr key={e.id} className="hover:bg-slate-50/60 transition-colors">
                        <td className="px-6 py-4 text-sm font-black text-slate-300">{idx + 1}</td>
                        <td className="px-4 py-4">
                          <div className="font-[900] text-slate-900 text-sm">{e.member?.name ?? `User #${e.user_id}`}</div>
                          {e.member?.membership_no && (
                            <div className="text-[10px] font-black uppercase tracking-widest text-indigo-600 mt-0.5">
                              {e.member.membership_no}
                            </div>
                          )}
                          <div className="text-xs text-slate-400 font-medium">{e.member?.email}</div>
                        </td>
                        <td className="px-4 py-4">
                          <div className="text-sm font-bold text-slate-700">{e.event?.title ?? e.notes ?? `Entry #${e.id}`}</div>
                          {e.notes && e.event && <div className="text-xs text-slate-400">{e.notes}</div>}
                        </td>
                        <td className="px-4 py-4 hidden md:table-cell">
                          <span className="text-sm text-slate-500 font-medium">{fmt(e.activity_date)}</span>
                        </td>
                        <td className="px-4 py-4">
                          <div className="flex flex-wrap gap-1">
                            <span className={`text-[10px] font-black uppercase tracking-widest px-2.5 py-1 rounded-lg border ${TYPE_BADGE[e.activity_type] ?? "bg-slate-100 text-slate-500 border-slate-200"}`}>
                              {e.activity_type}
                            </span>
                            {e.allocation_source && SOURCE_BADGE[e.allocation_source] && (
                              <span className={`text-[10px] font-black uppercase tracking-widest px-2.5 py-1 rounded-lg border ${SOURCE_BADGE[e.allocation_source].cls}`}>
                                {SOURCE_BADGE[e.allocation_source].label}
                              </span>
                            )}
                          </div>
                        </td>
                        <td className="px-6 py-4 text-right">
                          <span className={`font-black text-base ${e.activity_type === "deduction" ? "text-rose-600" : "text-violet-600"}`}>
                            {e.activity_type === "deduction" ? "−" : "+"}{Math.abs(e.points).toFixed(1)}
                          </span>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}
          </div>
        )}

        {/* ── TAB: LEADERBOARD ── */}
        {tab === "leaderboard" && (
          <>
            {loadingSummary ? (
              <div className="flex justify-center py-20"><Loader2 className="w-8 h-8 text-indigo-500 animate-spin" /></div>
            ) : (summary as any[]).length === 0 ? (
              <div className="bg-white border border-slate-100 rounded-[2rem] p-16 text-center">
                <Star className="w-10 h-10 text-slate-300 mx-auto mb-3" />
                <p className="text-slate-400 font-bold">No leaderboard data yet.</p>
              </div>
            ) : (
              <div className="bg-white border border-slate-100 rounded-[2rem] overflow-x-auto">
                <table className="w-full">
                  <thead>
                    <tr className="border-b border-slate-100 bg-slate-50/60">
                      <th className="text-left text-[10px] font-black text-slate-400 uppercase tracking-widest px-6 py-4 w-16">Rank</th>
                      <th className="text-left text-[10px] font-black text-slate-400 uppercase tracking-widest px-4 py-4">Member</th>
                      <th className="text-left text-[10px] font-black text-slate-400 uppercase tracking-widest px-4 py-4 hidden md:table-cell">Email</th>
                      <th className="text-left text-[10px] font-black text-slate-400 uppercase tracking-widest px-4 py-4 hidden lg:table-cell">Entries</th>
                      <th className="text-right text-[10px] font-black text-slate-400 uppercase tracking-widest px-6 py-4">Total Points</th>
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-slate-50">
                    {(summary as any[]).map((row: any, i: number) => (
                      <tr key={row.user_id} className="hover:bg-slate-50/60 transition-colors">
                        <td className="px-6 py-4">
                          <div className={`w-8 h-8 rounded-xl flex items-center justify-center font-black text-sm ${
                            i === 0 ? "bg-amber-50 text-amber-700" :
                            i === 1 ? "bg-slate-100 text-slate-600" :
                            i === 2 ? "bg-orange-50 text-orange-600" : "bg-slate-50 text-slate-400"
                          }`}>{i + 1}</div>
                        </td>
                        <td className="px-4 py-4">
                          <div className="font-[900] text-slate-900 text-sm">{row.name}</div>
                        </td>
                        <td className="px-4 py-4 hidden md:table-cell">
                          <span className="text-sm text-slate-500 font-medium">{row.email}</span>
                        </td>
                        <td className="px-4 py-4 hidden lg:table-cell">
                          <span className="text-sm font-bold text-slate-600">{row.entry_count} entr{row.entry_count === 1 ? "y" : "ies"}</span>
                        </td>
                        <td className="px-6 py-4 text-right">
                          <span className="font-black text-violet-600 text-base">{Number(row.total_points).toFixed(1)} pts</span>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}
          </>
        )}
      </div>

      {/* ── APPROVE MODAL ── */}
      {approveModal && (
        <div className="fixed inset-0 z-50 flex items-center justify-center p-4">
          <div className="absolute inset-0 bg-black/30 backdrop-blur-sm" onClick={() => setApproveModal(null)} />
          <div className="relative bg-white rounded-[2rem] shadow-2xl w-full max-w-md p-8 z-10">
            <div className="flex items-center justify-between mb-6">
              <h2 className="font-black text-slate-900 text-lg uppercase tracking-tight">Approve Submission</h2>
              <button onClick={() => setApproveModal(null)} className="p-2 rounded-xl hover:bg-slate-50">
                <X className="w-5 h-5 text-slate-400" />
              </button>
            </div>
            <p className="text-sm text-slate-500 font-medium mb-6">
              Approving: <span className="font-black text-slate-800">{approveModal.title}</span>
              {approveModal.hasEvent && approveModal.points != null && (
                <span className="ml-2 text-violet-600 font-black">— {approveModal.points} pts will be allocated</span>
              )}
            </p>
            <div className="space-y-5">
              {!approveModal.hasEvent && (
                <div className="bg-amber-50 border border-amber-100 rounded-2xl p-4 space-y-3">
                  <p className="text-xs text-amber-800 font-medium">
                    This is a <span className="font-black">manual CPD claim</span> (no event). The member self‑declared{" "}
                    <span className="font-black">{approveModal.claimedPoints ?? "—"} pts</span>
                    {approveModal.organizer ? ` · ${approveModal.organizer}` : ""}. Attach a CPD event —
                    the member is awarded <span className="font-black">that event's CPD points</span>, not the self‑declared figure.
                  </p>
                  <div>
                    <label className="text-[10px] font-black text-amber-500 uppercase tracking-widest">Attach CPD event *</label>
                    <select
                      value={approveEventId}
                      onChange={(e) => setApproveEventId(e.target.value)}
                      className="w-full mt-1 border-2 border-amber-200 rounded-xl px-3 py-2 text-sm font-bold text-slate-800 bg-white outline-none focus:border-amber-500"
                    >
                      <option value="">— Select a CPD event —</option>
                      {cpdEvents.map((ev: any) => (
                        <option key={ev.id} value={ev.id}>
                          {ev.title} ({ev.cpd_points ?? 0} pts)
                        </option>
                      ))}
                    </select>
                    {approveEventId && (
                      <p className="text-[11px] text-amber-700 mt-1 font-semibold">
                        Will award {cpdEvents.find((ev: any) => String(ev.id) === String(approveEventId))?.cpd_points ?? 0} CPD points.
                      </p>
                    )}
                  </div>
                </div>
              )}
              <div>
                <label className="text-[10px] font-black text-slate-400 uppercase tracking-widest">Notes (optional)</label>
                <textarea
                  rows={2}
                  value={approveNotes}
                  onChange={(e) => setApproveNotes(e.target.value)}
                  className="w-full border-b-2 border-slate-100 py-2 font-bold text-slate-800 outline-none focus:border-slate-900 bg-transparent text-sm resize-none"
                  placeholder="Any remarks for the member…"
                />
              </div>
              {actionError && (
                <div className="flex items-center gap-2 bg-rose-50 border border-rose-100 text-rose-700 text-sm font-medium px-4 py-3 rounded-2xl">
                  <AlertCircle className="w-4 h-4 shrink-0" /> {actionError}
                </div>
              )}
            </div>
            <div className="flex gap-3 mt-8">
              <button onClick={() => setApproveModal(null)} className="flex-1 py-3 rounded-2xl border-2 border-slate-100 text-sm font-black text-slate-500 hover:bg-slate-50">
                Cancel
              </button>
              <button
                onClick={handleApprove}
                disabled={approving}
                className="flex-[2] py-3 rounded-2xl bg-emerald-600 text-white text-sm font-black hover:bg-emerald-700 transition-all flex items-center justify-center gap-2 disabled:opacity-50"
              >
                {approving ? <Loader2 className="w-4 h-4 animate-spin" /> : <CheckCircle2 className="w-4 h-4" />}
                Approve & Allocate
              </button>
            </div>
          </div>
        </div>
      )}

      {/* ── REJECT MODAL ── */}
      {rejectModal && (
        <div className="fixed inset-0 z-50 flex items-center justify-center p-4">
          <div className="absolute inset-0 bg-black/30 backdrop-blur-sm" onClick={() => setRejectModal(null)} />
          <div className="relative bg-white rounded-[2rem] shadow-2xl w-full max-w-md p-8 z-10">
            <div className="flex items-center justify-between mb-6">
              <h2 className="font-black text-slate-900 text-lg uppercase tracking-tight">Reject Submission</h2>
              <button onClick={() => setRejectModal(null)} className="p-2 rounded-xl hover:bg-slate-50">
                <X className="w-5 h-5 text-slate-400" />
              </button>
            </div>
            <p className="text-sm text-slate-500 font-medium mb-6">
              Rejecting: <span className="font-black text-slate-800">{rejectModal.title}</span>
            </p>
            <div>
              <label className="text-[10px] font-black text-slate-400 uppercase tracking-widest">Reason (optional)</label>
              <textarea
                rows={3}
                value={rejectNotes}
                onChange={(e) => setRejectNotes(e.target.value)}
                className="w-full border-b-2 border-slate-100 py-2 font-bold text-slate-800 outline-none focus:border-slate-900 bg-transparent text-sm resize-none mt-2"
                placeholder="e.g. Certificate is not legible, please resubmit…"
                autoFocus
              />
            </div>
            {actionError && (
              <div className="flex items-center gap-2 bg-rose-50 border border-rose-100 text-rose-700 text-sm font-medium px-4 py-3 rounded-2xl mt-4">
                <AlertCircle className="w-4 h-4 shrink-0" /> {actionError}
              </div>
            )}
            <div className="flex gap-3 mt-8">
              <button onClick={() => setRejectModal(null)} className="flex-1 py-3 rounded-2xl border-2 border-slate-100 text-sm font-black text-slate-500 hover:bg-slate-50">
                Cancel
              </button>
              <button
                onClick={handleReject}
                disabled={rejecting}
                className="flex-[2] py-3 rounded-2xl bg-rose-600 text-white text-sm font-black hover:bg-rose-700 transition-all flex items-center justify-center gap-2 disabled:opacity-50"
              >
                {rejecting ? <Loader2 className="w-4 h-4 animate-spin" /> : <X className="w-4 h-4" />}
                Confirm Rejection
              </button>
            </div>
          </div>
        </div>
      )}

      {/* ── MANUAL ENTRY SLIDE-IN ── */}
      {showForm && (
        <div className="fixed inset-0 z-50 flex">
          <div className="flex-1 bg-black/30 backdrop-blur-sm" onClick={() => setShowForm(false)} />
          <div className="w-full max-w-md bg-white h-full overflow-y-auto shadow-2xl flex flex-col">
            <div className="p-7 border-b border-slate-100 flex items-center justify-between sticky top-0 bg-white z-10">
              <h2 className="font-black text-slate-900 text-lg uppercase tracking-tight">Manual CPD Entry</h2>
              <button onClick={() => setShowForm(false)} className="p-2 rounded-xl hover:bg-slate-50">
                <X className="w-5 h-5 text-slate-400" />
              </button>
            </div>
            <div className="p-7 space-y-5 flex-1">
              <div>
                <label className="text-[10px] font-black text-slate-400 uppercase tracking-widest">User ID *</label>
                <input type="number" className={inputCls} value={form.user_id} onChange={set("user_id")} placeholder="Member user ID" />
              </div>
              <div>
                <label className="text-[10px] font-black text-slate-400 uppercase tracking-widest">Points *</label>
                <input type="number" step="0.5" className={inputCls} value={form.points} onChange={set("points")} placeholder="e.g. 5 or -2" />
              </div>
              <div>
                <label className="text-[10px] font-black text-slate-400 uppercase tracking-widest">Type *</label>
                <select className={inputCls} value={form.activity_type} onChange={set("activity_type") as any}>
                  <option value="manual">Manual Award</option>
                  <option value="deduction">Deduction</option>
                </select>
              </div>
              <div>
                <label className="text-[10px] font-black text-slate-400 uppercase tracking-widest">Activity Date *</label>
                <input type="date" className={inputCls} value={form.activity_date} onChange={set("activity_date")} />
              </div>
              <div>
                <label className="text-[10px] font-black text-slate-400 uppercase tracking-widest">Notes</label>
                <textarea className="w-full border-b-2 border-slate-100 py-2 font-bold text-slate-800 outline-none focus:border-slate-900 bg-transparent text-sm resize-none" rows={3} value={form.notes} onChange={set("notes")} placeholder="Reason / description…" />
              </div>
              {formError && (
                <div className="flex items-center gap-2 bg-rose-50 border border-rose-100 text-rose-700 text-sm font-medium px-4 py-3 rounded-2xl">
                  <AlertCircle className="w-4 h-4 shrink-0" /> {formError}
                </div>
              )}
            </div>
            <div className="p-7 border-t border-slate-50 sticky bottom-0 bg-white">
              <button
                onClick={handleCreateEntry}
                disabled={creating}
                className="w-full bg-slate-900 text-white font-black text-sm py-3.5 rounded-2xl hover:bg-primary disabled:opacity-50 transition-colors flex items-center justify-center gap-2"
              >
                {creating ? <Loader2 className="w-4 h-4 animate-spin" /> : <CheckCircle2 className="w-4 h-4" />}
                Create Entry
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

/**
 * Reading CPD ledgers; awarding and approving are gated separately inside.
 */
export default function AdminCpdPage() {
  return (
    <PageGate perm={PERMS.event.cpdAccess}>
      <AdminCpdPageInner />
    </PageGate>
  );
}
